import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({
  user: null,
  session: null,
  loading: true,
  signOut: () => Promise.resolve(),
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const initSession = async () => {
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (isMounted) {
          const currentUser = initialSession?.user ?? null;
          
          if (currentUser) {
            setSession(initialSession);
            setUser(currentUser);
          } else {
            setSession(null);
            setUser(null);
          }
        }
      } catch (err) {
        console.error('Auth init error:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      if (isMounted) {
        console.log("Auth State Change Event:", event);
        const currentUser = currentSession?.user ?? null;

        if (currentUser) {
          setSession(currentSession);
          setUser(currentUser);
        } else {
          setSession(null);
          setUser(null);
        }
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      // Clear active authentication session from Supabase
      await supabase.auth.signOut();
      
      // Clear React auth state immediately
      setUser(null);
      setSession(null);
      
      // IMPORTANT: Do NOT clear remembered credentials here
      // Remember me data (remembered_email, remember_me) should persist across logout
      // Only clear if user manually unchecks "Remember Me" on login page
    } catch (error) {
      console.error('Sign out error:', error);
      // Still clear local state even if signOut fails
      setUser(null);
      setSession(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
